import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
    BiddingService,
    PostBid201Response,
    PostBidLotState,
    PostBidRequest,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import { BiddingErrorCodes } from '../../modules/profiles/enums/bidding-error-codes';

export interface BidLabels {
    isQuickBid: boolean;
    isProxy: boolean;
}

/** deriveBidLabels compares against this, and vb-lot-card / vb-bid-panel post
 *  it as the quick bid amount, so the three must not drift apart. */
export function nextBidAmountFor(lot: {
    currentPrice?: number | null;
    bidIncrement?: number | null;
    minBidAmount?: number | null;
}): number {
    const current: number = lot.currentPrice ?? 0;
    const increment: number = lot.bidIncrement ?? 0;
    const minBid: number = lot.minBidAmount ?? 0;
    return current > 0 ? current + increment : minBid;
}

export function deriveBidLabels(
    amount: number,
    minimumBidAmount: number,
): BidLabels {
    const bid: number = Number(amount);
    const minimum: number = Number(minimumBidAmount);
    return {
        isQuickBid: bid === minimum,
        isProxy: bid > minimum,
    };
}

export interface PlaceLotBidInput {
    auctionId: string;
    auctionLotsId: string;
    lotId?: string;
    lotTitle: string;
    lotNumber?: string | number;
    buyerPremium: number;
    amount: number;
    minimumBidAmount: number;
    pageName:
        | 'LOT_GRID'
        | 'LOT_LIST'
        | 'WATCHLIST_GRID'
        | 'WATCHLIST_LIST'
        | 'LOT_DETAILS'
        // The profile My-bidding ledger's historical analytics name.
        | 'LOT_DISPLAY';
    pageLocation: 'QUICK_BID' | 'MAIN_INPUT_FIELD';
}

@Injectable({ providedIn: 'root' })
export class LotCardBidService {
    private successToastId: number = 0;

    constructor(
        private readonly biddingService: BiddingService,
        private readonly snackbarService: VbSnackbarService,
        private readonly translateService: TranslateService,
        private readonly biddingUtils: BiddingUtils,
        private readonly modalService: BsModalService,
    ) {}

    /**
     * Places a bid that has already been confirmed by the caller's own UI
     * (every page stages bids through the vb-bid-confirm-dialog first).
     * Reuses the shared post/toast/error handling.
     */
    placeConfirmedBid(
        input: PlaceLotBidInput,
        currentUserId: string,
        onComplete?: (placed: boolean, lotState?: PostBidLotState) => void,
    ): void {
        this.postBid(input, currentUserId, onComplete);
    }

    private postBid(
        input: PlaceLotBidInput,
        currentUserId: string,
        onComplete?: (placed: boolean, lotState?: PostBidLotState) => void,
    ): void {
        const metaData: Record<string, unknown> = {
            user: currentUserId,
            pageName: input.pageName,
            lotId: input.lotId,
            auctionId: input.auctionId,
            bidAmount: input.amount,
            createdOn: new Date().toISOString(),
            pageLocation: input.pageLocation,
        };

        const labels: BidLabels = deriveBidLabels(
            input.amount,
            input.minimumBidAmount,
        );

        const request: PostBidRequest = {
            auctionId: input.auctionId,
            auctionLotId: input.auctionLotsId,
            amount: Number(input.amount),
            isProxy: labels.isProxy,
            isQuickBid: labels.isQuickBid,
            isBuyNow: false,
            metaData: JSON.stringify(metaData),
        };

        this.biddingService.postBid(request).subscribe({
            next: (response: PostBid201Response) => {
                if (response.accepted === false || this.isErrorBody(response)) {
                    this.handleError(
                        { error: response } as HttpErrorResponse,
                        onComplete,
                    );
                    return;
                }
                this.snackbarService.dismiss(this.successToastId);
                this.successToastId = this.snackbarService.success(
                    this.translateService.instant('Bidding.biddingSuccess', {
                        lotNumber: input.lotNumber,
                    }),
                );
                if (response.data['accountMappingsId'] !== currentUserId) {
                    response.data['accountMappingsId'] = currentUserId;
                }
                this.biddingUtils.userBids.push(response.data);
                onComplete?.(true, response.lotState);
            },
            error: (error: HttpErrorResponse) => {
                this.handleError(error, onComplete);
            },
        });
    }

    private isErrorBody(response: PostBid201Response | undefined): boolean {
        if (!response) return false;
        const r: Record<string, unknown> = response as unknown as Record<
            string,
            unknown
        >;
        return (
            r['isErrorResponse'] === true ||
            r['error'] === true ||
            typeof r['errorCode'] === 'number'
        );
    }

    private handleError(
        error: HttpErrorResponse,
        onComplete?: (placed: boolean, lotState?: PostBidLotState) => void,
    ): void {
        if (
            error.error?.errorCode === BiddingErrorCodes.LOT_UNDER_MAINTENANCE
        ) {
            this.biddingUtils.showBidMaintenanceDialog(
                this.modalService,
                this.translateService.instant('Bidding.maintenanceDialogTitle'),
                this.translateService.instant(
                    'Bidding.maintenanceDialogMessage',
                ),
                this.translateService.instant(
                    'Bidding.maintenanceDialogButtonLabel',
                ),
            );
            onComplete?.(false);
            return;
        }
        const errorMessage: string = this.biddingUtils.getErrorMessage(error);
        this.snackbarService.error(errorMessage);
        onComplete?.(false);
    }
}
